import { MathUtils, Vector3 } from 'three';
import { calculateWallLength } from './vectorUtils';
export const ROOM_SHAPE_STRICTNESS_FACTOR = 1.5;

// Function to check if the room can be a regular shape (rectangle/square)
export const canFormRegularRoom = (A: number, B: number, C: number, D: number): boolean => A === C && B === D; // A valid rectangle or square if opposite sides are equal

// Function to check if the room can be formed as a U-shape with two 90-degree angles and different wall lengths
export const canCreateRoomWithTwoNinetyDegreeCorners = (
  A: number,
  B: number,
  C: number,
  D: number,
): { isPossible: boolean; possiblePermutations?: Array<{ pOne: string; pTwo: string }> } => {
  const permutationsSets: Array<{ pOne: string; pTwo: string }> = [
    { pOne: 'A', pTwo: 'B' },
    { pOne: 'B', pTwo: 'C' },
    { pOne: 'C', pTwo: 'D' },
    { pOne: 'D', pTwo: 'A' },
  ];

  const result: Array<{ pOne: string; pTwo: string }> = [];
  for (const permutationSet of permutationsSets) {
    const L1 = { A, B, C, D }[permutationSet.pOne];
    const L2 = { A, B, C, D }[permutationSet.pTwo];

    // Calculate diagonal length (is the minimum size the rest of the walls have to comprise).
    const diagonal = Math.sqrt(L1! * L1! + L2! * L2!);

    const remainingSides = ['A', 'B', 'C', 'D'].filter((side) => side !== permutationSet.pOne && side !== permutationSet.pTwo);

    const s = { A, B, C, D }[remainingSides[0]];
    const t = { A, B, C, D }[remainingSides[1]];

    if (s! + t! > diagonal && s! + diagonal > t! && t! + diagonal > s!) {
      result.push(permutationSet);
    }
  }

  if (result.length !== 0) {
    return { isPossible: true, possiblePermutations: result };
  }

  return { isPossible: false };
};

export const findCircleIntersections = (x1: number, y1: number, r1: number, x2: number, y2: number, r2: number): Vector3[] | null => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const d = Math.sqrt(dx * dx + dy * dy);

  if (d > r1 + r2 || d < Math.abs(r1 - r2)) {
    return null;
  }

  const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
  const h = Math.sqrt(r1 * r1 - a * a);
  const xm = x1 + (dx * a) / d;
  const ym = y1 + (dy * a) / d;

  const xs1 = xm + (h * dy) / d;
  const ys1 = ym - (h * dx) / d;
  const xs2 = xm - (h * dy) / d;
  const ys2 = ym + (h * dx) / d;

  if (d === r1 + r2 || d === Math.abs(r1 - r2)) {
    return [new Vector3(xs1, ys1, 0)];
  }

  return [new Vector3(xs1, ys1, 0), new Vector3(xs2, ys2, 0)];
};

function findIntersections(x1: number, y1: number, r1: number, x2: number, y2: number, r2: number): Array<Vector3> {
  const AB0 = x2 - x1;
  const AB1 = y2 - y1;
  const c = Math.sqrt(AB0 * AB0 + AB1 * AB1);
  if (c === 0) {
    // no distance between centers
    return [];
  }
  const x = (r1 * r1 + c * c - r2 * r2) / (2 * c);
  let y = r1 * r1 - x * x;
  if (y < 0) {
    // no intersection
    return [];
  }
  if (y > 0) y = Math.sqrt(y);
  // compute unit vectors ex and ey
  const ex0 = AB0 / c;
  const ex1 = AB1 / c;
  const ey0 = -ex1;
  const ey1 = ex0;
  let Q1x = x1 + x * ex0;
  let Q1y = y1 + x * ex1;
  if (y === 0) {
    // one touch point
    return [new Vector3(Q1x, Q1y, 0)];
  }
  // two intersections
  const Q2x = Q1x - y * ey0;
  const Q2y = Q1y - y * ey1;
  Q1x += y * ey0;
  Q1y += y * ey1;
  return [new Vector3(Q1x, Q1y, 0), new Vector3(Q2x, Q2y, 0)];
}

// Function to create an exact quadrilateral
export const createExactQuadrilateral = (A: number, B: number, C: number, D: number): Vector3[] => {
  const pointP1 = new Vector3(0, 0, 0); // A
  const pointP2 = new Vector3(A, 0, 0); // B

  const pointP3 = findCircleIntersections(pointP1.x, pointP1.y, D, pointP2.x, pointP2.y, B);
  if (!pointP3) {
    return []; // Return empty if no intersection is found
  }

  const pointP4 = findCircleIntersections(pointP3[0].x, pointP3[0].y, C, pointP2.x, pointP2.y, B);
  if (!pointP4) {
    return []; // Return empty if no intersection is found
  }

  return [pointP1, pointP2, pointP4[0], pointP3[0]];
};

// Function to create a simple regular room (Rectangle or Square)
export const createRegularRoom = (A: number, B: number): Vector3[] => [
  new Vector3(0, 0, 0), // Point A (origin)
  new Vector3(A, 0, 0), // Point B (move A units right)
  new Vector3(A, -B, 0), // Point C (move B units down)
  new Vector3(0, -B, 0), // Point D (back to origin along x)
];

// Generate a two ninety-degree corners room if possible; Generate a one-degree corner room as a fallback.
export const createRoomWithTwoNintyDegressCorners = (A: number, B: number, C: number, D: number): Array<Vector3> => {
  // helper function for validation
  const _validate = (points: Array<Vector3>, A: number, B: number, C: number, D: number) => {
    const pointA = points[0];
    const pointB = points[1];
    const pointC = points[2];
    const pointD = points[3];

    return (
      parseFloat(calculateWallLength(pointA, pointB).toFixed(2)) === A &&
      parseFloat(calculateWallLength(pointB, pointC).toFixed(2)) === B &&
      parseFloat(calculateWallLength(pointC, pointD).toFixed(2)) === C &&
      parseFloat(calculateWallLength(pointD, pointA).toFixed(2)) === D
    );
  };

  // Helper function to evaluate the score of the related room
  const _evaluateScore = (points: Array<Vector3>): { points: Array<Vector3>; numberOfRightAngle: number; area: number } => {
    const pointA = points[0];
    const pointB = points[1];
    const pointC = points[2];
    const pointD = points[3];

    // Building substraction vectors for cross product
    const vectors = [
      [new Vector3().subVectors(pointB, pointA), new Vector3().subVectors(pointC, pointB)],
      [new Vector3().subVectors(pointC, pointB), new Vector3().subVectors(pointD, pointC)],
      [new Vector3().subVectors(pointD, pointC), new Vector3().subVectors(pointA, pointD)],
      [new Vector3().subVectors(pointA, pointD), new Vector3().subVectors(pointB, pointA)],
      [new Vector3().subVectors(pointA, pointC), new Vector3().subVectors(pointB, pointD)],
    ];

    // Calculating rightAngle using a minimum tolerance value
    let rightAngleCount = 0;
    const tolerance = MathUtils.degToRad(0.5);
    for (const [v1, v2] of vectors) {
      const dot = v1.dot(v2);
      const magnitudeProduct = v1.length() * v2.length();

      if (magnitudeProduct === 0) continue;

      const angle = Math.acos(dot / magnitudeProduct);
      if (Math.abs(angle - Math.PI / 2) <= tolerance) {
        rightAngleCount++;
      }
    }

    // Calculating are of the shape using Gaussian trapezoidal formula
    const diagonalAC = new Vector3().subVectors(pointC, pointA);
    const diagonalBD = new Vector3().subVectors(pointD, pointB);
    const crossProduct = new Vector3().crossVectors(diagonalAC, diagonalBD);
    const area = 0.5 * crossProduct.length();
    return { points: points, numberOfRightAngle: rightAngleCount, area: area };
  };

  const resultCollection: Array<Array<Vector3>> = [];
  const regularStartPointA: Vector3 = new Vector3(0, 0, 0);
  const regularStartPointB: Vector3 = new Vector3(A, 0, 0);
  const regularStartPointC: Vector3 = new Vector3(A, -B, 0);
  const regularStartPointD: Vector3 = new Vector3(0, -D, 0);

  // Calculating intersecting points pointA
  const intersectionPointsA = findIntersections(regularStartPointB.x, regularStartPointB.y, A, regularStartPointD.x, regularStartPointD.y, D);
  if (intersectionPointsA) {
    for (let i = 0; i < intersectionPointsA.length; i++) {
      const possibleIntersectionPoint = intersectionPointsA[i];
      const tmp = [possibleIntersectionPoint, regularStartPointB, regularStartPointC, regularStartPointD];
      if (_validate(tmp, A, B, C, D)) {
        resultCollection.push(tmp);
      }
    }
  }

  // Calculating intersecting points pointB
  const intersectionPointsB = findIntersections(regularStartPointC.x, regularStartPointC.y, B, regularStartPointA.x, regularStartPointA.y, A);
  if (intersectionPointsB) {
    for (let i = 0; i < intersectionPointsB.length; i++) {
      const possibleIntersectionPoint = intersectionPointsB[i];
      const tmp = [regularStartPointA, possibleIntersectionPoint, regularStartPointC, regularStartPointD];
      if (_validate(tmp, A, B, C, D)) {
        resultCollection.push(tmp);
      }
    }
  }

  // Calculating intersecting points pointC
  const intersectionPointsC = findIntersections(regularStartPointB.x, regularStartPointB.y, B, regularStartPointD.x, regularStartPointD.y, C);
  if (intersectionPointsC) {
    for (let i = 0; i < intersectionPointsC.length; i++) {
      const possibleIntersectionPoint = intersectionPointsC[i];
      const tmp = [regularStartPointA, regularStartPointB, possibleIntersectionPoint, regularStartPointD];
      if (_validate(tmp, A, B, C, D)) {
        resultCollection.push(tmp);
      }
    }
  }

  // Calculating intersecting points pointD
  const intersectionPointsD = findIntersections(regularStartPointA.x, regularStartPointA.y, D, regularStartPointC.x, regularStartPointC.y, C);
  if (intersectionPointsD) {
    for (let i = 0; i < intersectionPointsD.length; i++) {
      const possibleIntersectionPoint = intersectionPointsD[i];
      const tmp = [regularStartPointA, regularStartPointB, regularStartPointC, possibleIntersectionPoint];
      if (_validate(tmp, A, B, C, D)) {
        resultCollection.push(tmp);
      }
    }
  }

  // Building score of the validated results; taking the best scored result as reponse
  const resultWithScore = resultCollection
    .map((rC) => _evaluateScore(rC))
    .sort((a, b) => {
      if (b.numberOfRightAngle !== a.numberOfRightAngle) {
        return b.numberOfRightAngle - a.numberOfRightAngle;
      }
      return b.area - a.area;
    });
  return resultWithScore.length !== 0 ? resultWithScore[0].points : [];
};

// Function to check if the lengths form a valid quadrilateral
export const canFormQuadrilateral = (A: number, B: number, C: number, D: number): boolean => A + B + C > D && A + B + D > C && A + C + D > B && B + C + D > A;

// Function to check if the room can be a valid closed shape
export const canFormClosedRoom = (A: number, B: number, C: number, D: number): boolean => {
  if (A <= 0 || B <= 0 || C <= 0 || D <= 0) return false;

  return canFormQuadrilateral(A, B, C, D);
};

// Function to handle complex room shapes
export const createComplexRoom = (A: number, B: number, C: number, D: number): Vector3[] =>
  // generic quadrilateral
  createExactQuadrilateral(A, B, C, D);
